import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Smartphone, ExternalLink, Copy, Search, Loader2, UserCheck, Phone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { fetchPatients, Patient } from "@/services/patient-service";
import { formatPhone } from "@/utils/formatters";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clinicId: string;
  twilioWhatsappFrom: string;
  clinicTradeName?: string;
}

const TEMPLATE =
  "{nome}! 👋\n\n" +
  "Aqui é o canal automatizado da *{clinica}* — agendamento, confirmação e remarcação em poucos toques, 24h.\n\n" +
  "📞 {numero_display}\n\n" +
  "Por aqui você pode:\n" +
  "✓ Agendar uma nova consulta\n" +
  "✓ Confirmar, remarcar ou cancelar\n" +
  "✓ Ver convênios, endereço e horários\n" +
  "✓ Avisar se vai atrasar\n\n" +
  "Toca pra começar 👉 {wame_url}";

function digitsOnly(s: string): string {
  return (s || "").replace(/\D/g, "");
}

function buildText(patientName: string | null, twilioFrom: string, clinicTradeName?: string): string {
  const firstName = patientName ? patientName.split(/\s+/)[0] : "";
  const nomeToken = firstName ? `Olá ${firstName}` : "Olá";
  const botDigits = digitsOnly(twilioFrom);
  const local = botDigits.startsWith("55") ? botDigits.slice(2) : botDigits;
  const display = formatPhone(local) || botDigits;
  const wame = `https://wa.me/${botDigits}?text=Oi`;
  return TEMPLATE
    .replace(/\{nome\}/g, nomeToken)
    .replace(/\{clinica\}/g, clinicTradeName || "nossa clínica")
    .replace(/\{numero_display\}/g, display)
    .replace(/\{wame_url\}/g, wame);
}

function looksLikePhone(q: string): boolean {
  const d = digitsOnly(q);
  return d.length >= 3 && d.length / Math.max(q.length, 1) > 0.5;
}

export function InviteAutomationStandaloneDialog({
  open,
  onOpenChange,
  clinicId,
  twilioWhatsappFrom,
  clinicTradeName,
}: Props) {
  const { toast } = useToast();

  const [searchInput, setSearchInput]         = useState("");
  const [searchResults, setSearchResults]     = useState<Patient[]>([]);
  const [searching, setSearching]             = useState(false);
  const [searchDone, setSearchDone]           = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [confirmedPhone, setConfirmedPhone]   = useState("");
  const [text, setText]                       = useState("");

  useEffect(() => {
    if (open) {
      setSearchInput("");
      setSearchResults([]);
      setSearchDone(false);
      setSelectedPatient(null);
      setConfirmedPhone("");
      setText(buildText(null, twilioWhatsappFrom, clinicTradeName));
    }
  }, [open, twilioWhatsappFrom, clinicTradeName]);

  useEffect(() => {
    setText(buildText(selectedPatient?.name || null, twilioWhatsappFrom, clinicTradeName));
  }, [selectedPatient, twilioWhatsappFrom, clinicTradeName]);

  useEffect(() => {
    if (selectedPatient || confirmedPhone) return;
    const q = searchInput.trim();
    if (q.length < 3) { setSearchResults([]); setSearchDone(false); return; }
    const handle = setTimeout(async () => {
      setSearching(true);
      try {
        const isPhone = looksLikePhone(q);
        const results = await fetchPatients(
          false, false, undefined, 1, 8,
          isPhone ? undefined : q,
          undefined, undefined, undefined,
          isPhone ? digitsOnly(q) : undefined,
          clinicId,
        );
        setSearchResults(results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
        setSearchDone(true);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput, selectedPatient, confirmedPhone, clinicId]);

  const rawSearchDigits = digitsOnly(searchInput);
  const canConfirmRawPhone =
    !selectedPatient &&
    !confirmedPhone &&
    searchDone &&
    !searching &&
    searchResults.length === 0 &&
    (rawSearchDigits.length === 10 || rawSearchDigits.length === 11);

  const targetCellphone = useMemo(() => {
    if (selectedPatient?.cellphone) return digitsOnly(selectedPatient.cellphone);
    if (confirmedPhone) return confirmedPhone;
    return "";
  }, [selectedPatient, confirmedPhone]);

  const canSend = targetCellphone.length === 11 && text.trim().length > 0;

  const handleConfirmRawPhone = () => {
    setConfirmedPhone(rawSearchDigits);
  };

  const handleReset = () => {
    setSelectedPatient(null);
    setConfirmedPhone("");
    setSearchInput("");
    setSearchResults([]);
    setSearchDone(false);
  };

  const handleOpenWhatsApp = () => {
    if (!canSend) return;
    const url = `https://wa.me/55${targetCellphone}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast({ title: "WhatsApp aberto", description: "Revise e clique enviar pra completar." });
    onOpenChange(false);
  };

  const handleCopy = async () => {
    if (!text.trim()) return;
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Texto copiado", description: "Cole no WhatsApp pra enviar." });
    } catch {
      toast({ title: "Erro ao copiar", variant: "destructive" });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader className="border-b pb-2">
          <DialogTitle className="text-sm font-semibold uppercase tracking-wide flex items-center gap-2 text-primary">
            <Smartphone className="h-4 w-4" />
            Convidar pro Atendimento Automatizado
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 text-xs text-muted-foreground">
            Vai abrir o WhatsApp Web na conversa do paciente, com o texto já preenchido. <strong>Revise e clique enviar</strong> pra completar.
          </div>

          {!selectedPatient && !confirmedPhone && (
            <div className="space-y-1">
              <label className="filter-label">Buscar paciente (nome ou telefone)</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Maria silva ou 21988..."
                  className="h-8 pl-8 text-xs"
                />
                {searching && (
                  <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
                )}
              </div>
              {searchResults.length > 0 && (
                <div className="border rounded-md mt-1 max-h-44 overflow-y-auto bg-card">
                  {searchResults.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPatient(p)}
                      className="w-full text-left px-3 py-2 text-xs hover:bg-accent/10 border-b last:border-b-0 transition-colors"
                    >
                      <div className="font-medium">{p.name}</div>
                      <div className="text-muted-foreground tabular-nums">{formatPhone(p.cellphone || "") || "(sem telefone)"}</div>
                    </button>
                  ))}
                </div>
              )}
              {canConfirmRawPhone && (
                <div className="border border-dashed border-accent/40 bg-accent/5 rounded-md mt-2 px-3 py-2 space-y-2">
                  <div className="text-xs text-muted-foreground">
                    Nenhum paciente encontrado com esse número.
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="w-full btn-header"
                    onClick={handleConfirmRawPhone}
                  >
                    <Phone className="h-4 w-4 mr-1" />
                    Enviar convite pra {formatPhone(rawSearchDigits) || rawSearchDigits}
                  </Button>
                </div>
              )}
            </div>
          )}

          {selectedPatient && (
            <div className="space-y-1">
              <label className="filter-label">Paciente selecionado</label>
              <div className="flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-md px-3 py-2">
                <UserCheck className="h-4 w-4 text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{selectedPatient.name}</div>
                  <div className="text-[11px] text-muted-foreground tabular-nums">{formatPhone(selectedPatient.cellphone || "")}</div>
                </div>
                <Button variant="ghost" size="sm" className="text-[11px] h-6" onClick={handleReset}>
                  Trocar
                </Button>
              </div>
            </div>
          )}

          {confirmedPhone && (
            <div className="space-y-1">
              <label className="filter-label">Número informado</label>
              <div className="flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-md px-3 py-2">
                <Phone className="h-4 w-4 text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium tabular-nums">{formatPhone(confirmedPhone)}</div>
                  <div className="text-[11px] text-muted-foreground">Convite sem nome personalizado</div>
                </div>
                <Button variant="ghost" size="sm" className="text-[11px] h-6" onClick={handleReset}>
                  Trocar
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-1">
            <label className="filter-label">Texto do convite (editável)</label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={8}
              className="text-sm font-mono"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t">
          <Button variant="outline" size="sm" className="btn-header" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button variant="outline" size="sm" className="btn-header" onClick={handleCopy} disabled={!text.trim()}>
            <Copy className="h-4 w-4 mr-1" />
            Copiar texto
          </Button>
          <Button size="sm" className="btn-header" onClick={handleOpenWhatsApp} disabled={!canSend}>
            <ExternalLink className="h-4 w-4 mr-1" />
            Abrir no WhatsApp
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
